import { Box, Button, FormikTextInput, FormLabel, Modal } from '@palmetto/palmetto-components';
import { Field, FieldArray, Form, Formik, FormikHelpers } from 'formik';
import isNumber from 'lodash/isNumber';
import { useCallback, useMemo } from 'react';
import * as yup from 'yup';

import { Clientize, NonPlanPaymentEvents, PaymentPlan, PayoutEvent, PayoutEventMap, ProgramType } from 'types';

const paymentPlanSchema = yup.object({
  name: yup.string().required('Plan name is a required field.'),
  paymentSchedule: yup.mixed().test({
    test: function (value: unknown, context) {
      if (value === undefined) return true;
      const typedValue = value as Record<PayoutEvent, number>;
      const errors = Object.entries(typedValue).map(function ([key, value]: [string, number]) {
        if (value < 0 || value > 100) {
          return context.createError({
            message: 'Payment percentages must be between 0 and 100',
            path: `${context.path}.${key}`,
          });
        }
      });
      const filteredErrors = errors.filter(Boolean);
      if (filteredErrors.length > 0) {
        return filteredErrors[0];
      }
      if (
        Math.abs(100 - Object.values(typedValue).reduce((acc, event) => acc + (isNumber(event) ? event : 0), 0)) >
        0.00001
      ) {
        return context.createError({ message: 'Total payment must be 100%', path: 'total' });
      }
      return true;
    },
  }),
  pausedDays: yup.mixed().test({
    test: function (value: unknown, context) {
      const typedValue = value as Partial<Record<PayoutEvent, number | ''>>;
      const paymentSchedule = (context.parent as formValuesType).paymentSchedule;
      for (const [event, percent] of Object.entries(paymentSchedule ?? {})) {
        if (percent > 0) {
          const days = typedValue?.[event as PayoutEvent];
          if (days === undefined || days === '' || days < 0 || !Number.isInteger(days)) {
            return context.createError({
              message: 'Pause days is required and must be 0 or greater',
              path: `${context.path}.${event}`,
            });
          }
        }
      }
      return true;
    },
  }),
});

type formValuesType = {
  name: string;
  paymentSchedule: Partial<Record<PayoutEvent, number>>;
  pausedDays: Partial<Record<PayoutEvent, number>>;
};

export const PaymentPlanEditModal = ({
  isOpen,
  handleClose,
  handleSubmit,
  programType,
  modalTitle,
  submitButtonText,
  modalId,
  currentPaymentPlan,
}: {
  isOpen: boolean;
  handleClose: () => void;
  handleSubmit: (values: any) => Promise<void>;
  programType: ProgramType;
  modalTitle: string;
  submitButtonText: string;
  modalId: string;
  currentPaymentPlan?: Clientize<PaymentPlan>;
}) => {
  const initialValues: formValuesType = useMemo(() => {
    const allEventsPausedDaysDefault = PayoutEventMap[programType]
      .filter((entry) => !NonPlanPaymentEvents.includes(entry.event))
      .reduce(
        (acc, entry) => {
          acc[entry.event] = 0;
          return acc;
        },
        {} as Record<PayoutEvent, number>,
      );

    return {
      name: currentPaymentPlan?.name ?? '',
      paymentSchedule: currentPaymentPlan
        ? (currentPaymentPlan.paymentSchedule as PaymentPlan['paymentSchedule']).reduce(
            (acc, event) => {
              acc[event.event] = event.paymentPercent * 100;
              return acc;
            },
            {} as Record<PayoutEvent, number>,
          )
        : {},
      pausedDays: currentPaymentPlan
        ? (currentPaymentPlan.paymentSchedule as PaymentPlan['paymentSchedule']).reduce(
            (acc, event) => {
              acc[event.event] = event.pausedDays ?? 0;
              return acc;
            },
            { ...allEventsPausedDaysDefault },
          )
        : allEventsPausedDaysDefault,
    };
  }, [currentPaymentPlan, programType]);

  const onSubmit = useCallback(
    async (values: formValuesType, { setSubmitting, resetForm }: FormikHelpers<formValuesType>) => {
      const paymentPlan: Omit<PaymentPlan, 'id'> = {
        programType,
        name: values.name,
        paymentSchedule: Object.entries(values.paymentSchedule ?? {}).map(([event, paymentPercent]) => ({
          event: event as PayoutEvent,
          paymentPercent: (paymentPercent ?? 0) / 100,
          pausedDays: (paymentPercent ?? 0) > 0 ? ((values.pausedDays[event as PayoutEvent] as number) ?? 0) : 0,
        })),
      };
      await handleSubmit(paymentPlan);
      setSubmitting(false);
      resetForm();
    },
    [programType, handleSubmit],
  );
  const renderEventSchedule = (values: formValuesType) => () => (
    <Box childGap={{ base: 'lg', desktop: 'xl' }}>
      <Box direction={{ base: 'column', desktop: 'row' }}>
        <Box width="50">Payment Event</Box>
        <Box width="50" textAlign="right">
          Percentage Payment
        </Box>
      </Box>
      {PayoutEventMap[programType]
        .filter((entry) => !NonPlanPaymentEvents.includes(entry.event))
        .toSorted((a, b) => a.order - b.order)
        .map((entry) => (
          <Box childGap="sm" key={entry.name}>
            <Box direction={{ base: 'column', desktop: 'row' }}>
              <Box width="50" direction="row" alignItems="center">
                <FormLabel inputId={entry.event}>{entry.name}</FormLabel>
              </Box>
              <Box width="50">
                <Field
                  key={entry.event}
                  type="number"
                  name={`paymentSchedule.${entry.event}`}
                  id={`paymentSchedule.${entry.event}`}
                  component={FormikTextInput}
                  suffix="%"
                />
              </Box>
            </Box>
            {(values.paymentSchedule[entry.event] as number) > 0 && (
              <Box>
                <Field
                  type="number"
                  label="Pause Days"
                  name={`pausedDays.${entry.event}`}
                  id={`pausedDays.${entry.event}`}
                  component={FormikTextInput}
                  isRequired={true}
                  suffix="days"
                />
              </Box>
            )}
          </Box>
        ))}
    </Box>
  );

  const renderTotalPayment = useCallback((values: formValuesType) => {
    const total = Object.values(values.paymentSchedule ?? {}).reduce((acc, event) => {
      return acc + (event || 0);
    }, 0);
    return (
      <Box direction={{ base: 'column', desktop: 'row' }} padding="lg 0 0 0">
        <Box width="50">Total</Box>
        <Box width="50" textAlign="right" color={total === 100 ? 'body-primary' : 'danger'} childGap="md">
          <Box>{total?.toFixed?.(1)}%</Box>
          <Box height="1rem">
            {total !== 0 && total !== 100 && (
              <Box as="span" color="danger">
                Total must be 100%
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    );
  }, []);

  return (
    <Formik
      validationSchema={paymentPlanSchema}
      initialValues={initialValues}
      onSubmit={onSubmit}
      enableReinitialize={true}
      validateOnBlur
    >
      {({ isSubmitting, resetForm, submitForm, values }) => (
        <Form>
          <Modal
            isOpen={isOpen}
            maxWidth="4xl"
            ariaLabelledBy={modalId}
            onDismiss={() => {
              if (!isSubmitting) {
                resetForm();
                handleClose();
              }
            }}
          >
            <Modal.Header
              id={modalId}
              title={modalTitle}
              onDismiss={() => {
                if (!isSubmitting) {
                  resetForm();
                  handleClose();
                }
              }}
            />
            <Modal.Body background="secondary" childGap="lg">
              <Box childGap={{ base: 'lg', desktop: 'xl' }}>
                <Field
                  type="text"
                  label="Name"
                  name="name"
                  id="name"
                  component={FormikTextInput}
                  isRequired={true}
                />
                <Box borderWidth="xs" borderColor="grey-100" padding="md" radius="md">
                  {programType && (
                    <>
                      <FieldArray name="paymentSchedule" render={renderEventSchedule(values)} />
                      {renderTotalPayment(values)}
                    </>
                  )}
                </Box>
              </Box>
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="secondary"
                tone="neutral"
                isLoading={isSubmitting}
                isDisabled={isSubmitting}
                onClick={() => {
                  resetForm();
                  handleClose();
                }}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                isLoading={isSubmitting}
                isDisabled={isSubmitting}
                type="submit"
                onClick={submitForm}
              >
                {submitButtonText}
              </Button>
            </Modal.Footer>
          </Modal>
        </Form>
      )}
    </Formik>
  );
};
